"use client";

import { useMemo, useState } from "react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { CommandTraceView, type CommandTraceViewProps } from "@/components/headless/CommandTraceView";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { UnifiedApprovalRow } from "@/core/approvals/unified-approvals";
import { getEmptyListState } from "@/core/ui/empty-states";
import { getDemoApprovals } from "@/lib/demo-data";

const listPendingApprovals = makeFunctionReference<"query", { tenantId: string }, UnifiedApprovalRow[]>(
  "approvals:listUnifiedPending",
);
const decideApproval = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    id: string;
    source: UnifiedApprovalRow["source"];
    status: string;
    decisionNote?: string;
  },
  {
    id: string;
    source: UnifiedApprovalRow["source"];
    status: string;
    decidedBy: string;
    commandResult?: unknown;
    executionOutcome?: "executed" | "approved_no_executor" | "not_applicable";
  }
>("approvals:decideUnified");

export function ReviewerInbox({ tenantId }: { tenantId: string }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <ReviewerInboxPanel tenantId={tenantId} approvals={getDemoApprovals(tenantId)} />;
  }
  return <LiveReviewerInbox tenantId={tenantId} />;
}

function LiveReviewerInbox({ tenantId }: { tenantId: string }) {
  const approvals = useQuery(listPendingApprovals, { tenantId });
  if (approvals === undefined) {
    return (
      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_420px] gap-4 px-6 py-5">
        <QuerySkeleton surface="reviewerInbox" />
        <QuerySkeleton surface="agentTimeline" />
      </div>
    );
  }
  return <ReviewerInboxPanel tenantId={tenantId} approvals={approvals} />;
}

function ReviewerInboxPanel({
  tenantId,
  approvals,
}: {
  tenantId: string;
  approvals: UnifiedApprovalRow[];
}) {
  const [selectedId, setSelectedId] = useState(approvals[0]?.id);
  const [localStatuses, setLocalStatuses] = useState<Record<string, string>>({});
  const [lastDecision, setLastDecision] = useState<{
    title: string;
    status: string;
    simulated: boolean;
    noExecutor: boolean;
  } | null>(null);
  const rows = useMemo(
    () =>
      approvals.map((approval) => ({
        ...approval,
        status: localStatuses[approval.id] ?? approval.status,
      })),
    [approvals, localStatuses],
  );

  const selected = useMemo(
    () => rows.find((approval) => approval.id === selectedId) ?? rows[0],
    [rows, selectedId],
  );
  const selectedTrace = selected ? commandTraceFromApproval(selected) : null;
  const selectedReasons = selected ? splitReasons(selected.riskSummary) : [];
  const decide = useMutation(decideApproval);

  async function applyDecision(status: "approved" | "edited_and_approved" | "rejected" | "escalated") {
    if (!selected) return;
    if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
      setLocalStatuses((current) => ({ ...current, [selected.id]: status }));
      return;
    }
    // Identity is derived server-side from the authenticated membership.
    const result = await decide({
      tenantId,
      id: selected.id,
      source: selected.source,
      status,
    });
    setLastDecision({
      title: selected.title,
      status,
      simulated: hasSandboxMarker(result?.commandResult),
      noExecutor: result?.executionOutcome === "approved_no_executor",
    });
  }

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)_420px] gap-4 px-6 py-5">
      <section className="space-y-3">
        <div>
          <h1 className="text-xl font-semibold">Reviewer Inbox</h1>
          <p className="text-sm text-muted-foreground">Pending draft-and-hold actions.</p>
        </div>
        <Card>
          <CardContent className={approvals.length === 0 ? "p-4" : "p-0"}>
            {rows.length === 0 ? (
              <EmptyState {...getEmptyListState("approvals")} />
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Approval</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Age</th>
                    <th className="px-3 py-2">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((approval) => (
                    <tr
                      key={`${approval.source}:${approval.id}`}
                      className="cursor-pointer border-b border-border hover:bg-muted"
                      onClick={() => setSelectedId(approval.id)}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{approval.title}</span>
                          <Badge variant="neutral">{sourceLabel(approval.source)}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">{approval.loadId ?? "tenant-level"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="info">{approval.requestedAction}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={slaVariant(approval.createdAt)}>{ageLabel(approval.createdAt)}</Badge>
                      </td>
                      <td className="max-w-xs truncate px-3 py-2 text-muted-foreground">
                        {approval.riskSummary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </section>

      <aside>
        {lastDecision ? (
          <Card className="mb-3">
            <CardContent className="space-y-2 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{lastDecision.title}</span>
                <Badge variant={lastDecision.noExecutor ? "warn" : "good"}>{lastDecision.status}</Badge>
                {lastDecision.simulated ? <Badge variant="warn">Simulated — not sent</Badge> : null}
              </div>
              {lastDecision.noExecutor ? (
                <p className="text-xs text-amber-700">
                  Approved, but no executor is wired for this item — nothing was executed or sent. The gap is
                  recorded in the audit trail.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
        <Card className="sticky top-4">
          <CardHeader className="p-3">
            <CardTitle className="text-sm">{selected?.title ?? "Select approval"}</CardTitle>
          </CardHeader>
          {selected ? (
            <CardContent className="space-y-3 p-3 text-sm">
              {typeof selected.confidenceScore === "number" ? (
                <div className="flex items-center gap-2">
                  <Badge variant={confidenceVariant(selected.confidenceScore)}>
                    {selected.confidenceScore}% confidence
                  </Badge>
                  <Badge variant="neutral">{selected.requestedAction}</Badge>
                </div>
              ) : null}
              <div>
                <div className="text-xs uppercase text-muted-foreground">Risk</div>
                <p>{selected.riskSummary}</p>
              </div>
              {selectedReasons.length > 0 ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Approval reasons</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedReasons.map((reason) => (
                      <Badge key={reason} variant="warn">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
              <div>
                <div className="text-xs uppercase text-muted-foreground">Recommendation</div>
                <p>{selected.recommendation}</p>
              </div>
              {selectedTrace ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Command trace</div>
                  <div className="mt-1">
                    <CommandTraceView {...selectedTrace} />
                  </div>
                </div>
              ) : null}
              {selected.expectedOutcome ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Expected outcome</div>
                  <p>{selected.expectedOutcome}</p>
                </div>
              ) : null}
              {selected.evidence && selected.evidence.length > 0 ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Evidence</div>
                  <ul className="mt-1 space-y-1">
                    {selected.evidence.map((item, index) => (
                      <li key={index} className="rounded-md border border-border p-2">
                        <div className="font-medium">{item.title}</div>
                        {item.source ? <div className="text-xs text-muted-foreground">{item.source}</div> : null}
                        {item.summary ? <div className="text-xs text-muted-foreground">{item.summary}</div> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {selected.alternatives && selected.alternatives.length > 0 ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Alternatives</div>
                  <ul className="mt-1 list-disc space-y-1 pl-4 text-muted-foreground">
                    {selected.alternatives.map((alt, index) => (
                      <li key={index}>{alt}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <details className="rounded-md bg-muted p-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">Raw payload</summary>
                <pre className="mt-2 max-h-[280px] overflow-auto">{JSON.stringify(selected.draftPayload, null, 2)}</pre>
              </details>
              <div className="grid grid-cols-2 gap-2">
                <Button className="h-8" onClick={() => applyDecision("approved")}>
                  Approve
                </Button>
                <Button className="h-8" variant="secondary" onClick={() => applyDecision("edited_and_approved")}>
                  Edit & Approve
                </Button>
                <Button className="h-8" variant="secondary" onClick={() => applyDecision("rejected")}>
                  Reject
                </Button>
                <Button className="h-8" variant="secondary" onClick={() => applyDecision("escalated")}>
                  Escalate
                </Button>
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-3">
              <EmptyState className="p-4" {...getEmptyListState("approvals")} />
            </CardContent>
          )}
        </Card>
      </aside>
    </div>
  );
}

function confidenceVariant(score: number): "good" | "warn" | "bad" {
  if (score >= 70) return "good";
  if (score >= 40) return "warn";
  return "bad";
}

function slaVariant(createdAt: number): "good" | "warn" | "bad" {
  const ageMinutes = (Date.now() - createdAt) / 60_000;
  if (ageMinutes < 5) return "good";
  if (ageMinutes < 30) return "warn";
  return "bad";
}

function ageLabel(createdAt: number): string {
  const ageMinutes = Math.max(0, Math.round((Date.now() - createdAt) / 60_000));
  if (ageMinutes < 60) return `${ageMinutes}m`;
  return `${Math.round(ageMinutes / 60)}h`;
}

function sourceLabel(source: UnifiedApprovalRow["source"]) {
  if (source === "agentApprovalPacket") return "packet";
  if (source === "agentTask") return "task";
  return "approval";
}

function commandTraceFromApproval(approval: UnifiedApprovalRow): CommandTraceViewProps {
  const draft = isRecord(approval.draftPayload.draft) ? approval.draftPayload.draft : approval.draftPayload;
  const headless = isRecord(approval.draftPayload.headlessCommand)
    ? approval.draftPayload.headlessCommand
    : isRecord(draft.headlessCommand)
      ? draft.headlessCommand
      : {};
  const evidenceRefs =
    stringArray(headless.evidenceRefs) ??
    stringArray(approval.draftPayload.evidenceRefs) ??
    approval.evidence?.map((item) => item.title) ??
    [];

  return {
    traceId: stringValue(headless.traceId) ?? stringValue(approval.draftPayload.traceId) ?? `approval_${approval.id}`,
    status: approval.status,
    routeName: stringValue(headless.routeName) ?? "approval_queue",
    commandName: stringValue(headless.commandName) ?? approval.requestedAction,
    policyReasons: splitReasons(approval.riskSummary),
    evidenceRefs,
    payloadHash: stringValue(headless.payloadHash) ?? stringValue(approval.draftPayload.payloadHash) ?? `hash_${approval.id}`,
  };
}

function splitReasons(value: string): string[] {
  return value
    .split(/;|\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 5);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((item) => stringValue(item)).filter((item): item is string => Boolean(item));
  return items.length > 0 ? items : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Detects the provider sandbox marker (rawProviderResponse.sandbox === true)
 * anywhere inside a command result, meaning nothing external was sent. */
function hasSandboxMarker(value: unknown, depth = 0): boolean {
  if (!value || typeof value !== "object" || depth > 5) return false;
  const record = value as Record<string, unknown>;
  if (record.sandbox === true) return true;
  return Object.values(record).some((child) => hasSandboxMarker(child, depth + 1));
}
